/**
 * Google Analytics Module (TypeScript)
 * Handles tracking for marketing site metrics without Firebase dependencies
 */

// Configuration constants merged from legacy analytics.js
const GET_GA_ID = (): string => {
  // Vite environment variables
  if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
    return import.meta.env.VITE_GA_MEASUREMENT_ID
  }

  // Fallback for other environments if needed
  // @ts-ignore
  if (typeof process !== 'undefined' && process.env?.GA_MEASUREMENT_ID) {
    // @ts-ignore
    return process.env.GA_MEASUREMENT_ID
  }

  // Hardcoded Fallback
  return 'G-55H84N7ZK3'
}

const ANALYTICS_CONFIG = {
  gaMeasurementId: GET_GA_ID(),
  enabled: true,
  // Auto-detect debug mode based on hostname
  debug:
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('ipv4.fiddler')),

  customDimensions: {
    pageSection: 'custom_dimension1',
    userType: 'custom_dimension2',
    contentType: 'custom_dimension3'
  }
}

// Minimal GA4 gtag typings
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: any[]) => void
    __analyticsEvents?: Array<{ ts: number; event: string; parameters: Record<string, any> }>
    analytics?: {
      trackEvent: (eventName: string, parameters?: Record<string, any>) => void
      [key: string]: any
    }
  }
}

type AnalyticsConfig = typeof ANALYTICS_CONFIG

type CampaignInfo = {
  source: string | null
  medium: string | null
  name: string | null
  term: string | null
  content: string | null
  gclid: string | null
  fbclid: string | null
  msclkid: string | null
  final_path_segment: string | null
  referrer: string | null
} | null

export class AnalyticsManager {
  private gaId: string
  private config: AnalyticsConfig
  private isInitialized: boolean
  private campaignInfo: CampaignInfo

  constructor() {
    this.gaId = ANALYTICS_CONFIG.gaMeasurementId
    this.config = ANALYTICS_CONFIG
    this.isInitialized = false
    this.campaignInfo = null

    if (this.config.enabled) {
      this.init()
    }
  }

  init(): void {
    if (this.isInitialized || typeof window === 'undefined') return

    // Setup dev tools hooks even in debug mode
    this.isInitialized = true

    if (this.config.debug) {
      console.log('📊 Analytics Manager initialized in DEBUG mode. Requests will be suppressed.')
    }
    else {
      this.loadGAScript()
      this.initGA4()
    }

    // Capture campaign info and set user properties before first page_view
    this.captureCampaignAttribution()

    // Initial page view
    this.trackPageView()

    // Listeners
    this.setupEventListeners()
  }

  private loadGAScript(): void {
    if (window.gtag) return

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`
    document.head.appendChild(script)

    window.dataLayer = window.dataLayer || []
    window.gtag = function () {
      window.dataLayer!.push(arguments)
    }
    window.gtag('js', new Date())
    window.gtag('config', this.gaId)
  }

  private initGA4(): void {
    if (!window.gtag) return

    const configParams: Record<string, any> = {
      page_title: document.title,
      page_location: window.location.href,
      send_page_view: false
    }

    window.gtag('config', this.gaId, configParams)
  }

  private captureCampaignAttribution(): void {
    const info = this.extractCampaignInfoFromLocation()
    this.campaignInfo = info

    const userProps = this.getCampaignUserProperties()
    if (Object.keys(userProps).length > 0) {
      if (this.config.debug) {
        console.log('GA4 user_properties:', userProps)
      } else if (window.gtag) {
        window.gtag('set', 'user_properties', userProps)
      }
    }

    try {
      const key = 'campaign_attribution_sent'
      if (!sessionStorage.getItem(key)) {
        const params = this.getCampaignEventParameters()
        if (Object.keys(params).length > 0) {
          this.trackEvent('campaign_attribution', params)
        }
        sessionStorage.setItem(key, '1')
      }
    } catch {
      // ignore
    }
  }

  private extractCampaignInfoFromLocation(): CampaignInfo {
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    const getParam = (name: string) => params.get(name) || params.get(name.toLowerCase()!)

    const utm_source = getParam('utm_source')
    const utm_medium = getParam('utm_medium')
    const utm_campaign = getParam('utm_campaign')
    const utm_term = getParam('utm_term')
    const utm_content = getParam('utm_content')

    const gclid = getParam('gclid')
    const fbclid = getParam('fbclid')
    const msclkid = getParam('msclkid')

    const finalPathSegment = this.getFinalPathSegment(url.pathname)

    let derivedSource = utm_source
    if (!derivedSource) {
      if (gclid) derivedSource = 'google_ads'
      else if (fbclid) derivedSource = 'facebook_ads'
      else if (msclkid) derivedSource = 'microsoft_ads'
    }

    const derivedCampaign = utm_campaign || finalPathSegment || null

    const referrer = document.referrer || null

    return {
      source: derivedSource || null,
      medium: utm_medium || null,
      name: derivedCampaign,
      term: utm_term || null,
      content: utm_content || null,
      gclid: gclid || null,
      fbclid: fbclid || null,
      msclkid: msclkid || null,
      final_path_segment: finalPathSegment || null,
      referrer
    }
  }

  private getFinalPathSegment(pathname: string): string | null {
    if (!pathname) return null
    const parts = pathname.split('/').filter(Boolean)
    if (parts.length === 0) return null
    const last = parts[parts.length - 1]!
    if (last.includes('.')) return null
    return last
  }

  private getCampaignUserProperties(): Record<string, string> {
    if (!this.campaignInfo) return {}
    const props: Record<string, string> = {}
    const c = this.campaignInfo
    if (!c) return props
    if (c.source) props.campaign_source = c.source
    if (c.medium) props.campaign_medium = c.medium
    if (c.name) props.campaign_name = c.name
    if (c.final_path_segment) props.campaign_path_segment = c.final_path_segment
    return props
  }

  private getCampaignEventParameters(): Record<string, string> {
    if (!this.campaignInfo) return {}
    const params: Record<string, string> = {}
    const c = this.campaignInfo
    if (!c) return params
    if (c.source) params.campaign_source = c.source
    if (c.medium) params.campaign_medium = c.medium
    if (c.name) params.campaign_name = c.name
    if (c.term) params.campaign_term = c.term
    if (c.content) params.campaign_content = c.content
    if (c.gclid) params.gclid = c.gclid
    if (c.fbclid) params.fbclid = c.fbclid
    if (c.msclkid) params.msclkid = c.msclkid
    if (c.final_path_segment) params.campaign_path_segment = c.final_path_segment
    if (c.referrer) params.page_referrer = c.referrer
    return params
  }

  trackPageView(): void {
    this.trackEvent('page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_referrer: document.referrer
    })
  }

  private setupEventListeners(): void {
    this.trackClicks()
    this.trackNewsletterSubscriptions()
    this.trackSectionViews()
    this.trackTimeOnPage()
  }

  private trackClicks(): void {
    document.addEventListener('click', (evt) => {
      const target = evt.target as Element | null
      if (!target) return

      // Order Button Clicks
      const orderBtn = target.closest('[data-retailer-btn],[data-retailer]') as HTMLAnchorElement | null
      if (orderBtn) {
        this.handleOrderClick(orderBtn);
        return
      }

      // Nav Links
      const navLink = target.closest('nav a, [data-footer-nav] a, [data-order-nav-link], .order-nav-link') as HTMLAnchorElement | null
      if (navLink) {
        this.trackEvent('navigation_click', {
          link_text: (navLink.textContent || '').trim(),
          link_url: navLink.href,
          navigation_type: this.getNavigationType(navLink)
        });
        return
      }

      // Social / Platform Links
      const socialLink = target.closest('[data-social], .social-icon') as HTMLAnchorElement | null
      if (socialLink) {
        this.trackEvent('social_media_click', {
          platform: socialLink.getAttribute('data-social') || this.getSocialPlatform(socialLink),
          page_section: 'footer'
        });
        return
      }

      // Generic Outbound Links
      const link = target.closest('a') as HTMLAnchorElement | null
      if (link && link.href) {
        const isExternal =
          (link.href.startsWith('http://') || link.href.startsWith('https://')) &&
          !link.href.includes(window.location.hostname)

        if (isExternal) {
          this.trackOutboundLink(link.href, (link.textContent || '').trim() || link.href)
        }
      }
    })

    // const bookCover = document.querySelector<HTMLImageElement>('.book-cover img')
    // if (bookCover) {
    //   bookCover.addEventListener('click', () => {
    //     this.trackEvent('book_cover_click', { page_section: 'hero_section' });
    //   })
    // }
  }

  private handleOrderClick(button: HTMLAnchorElement): void {
    const retailer = button.getAttribute('data-retailer') || this.getRetailerName(button)
    const format = button.getAttribute('data-format') || 'unknown'
    const buttonText = (button.textContent || '').trim()
    const retailerUrl = button.href

    const isExternal =
      retailerUrl &&
      (retailerUrl.startsWith('http://') || retailerUrl.startsWith('https://')) &&
      !retailerUrl.includes(window.location.hostname)

    if (isExternal) {
      this.trackEvent('begin_checkout', {
        currency: 'USD',
        retailer,
        button_text: buttonText,
        page_section: 'order_section',
        item_name: "The New Rules of Women's Health",
        item_category: 'Book',
        format
      })
    }
    else {
      this.trackEvent('order_click', {
        retailer,
        button_text: buttonText,
        page_section: 'order_section',
        format
      })
    }
  }

  private getRetailerName(button: Element): string {
    const attr = (button as HTMLElement).getAttribute?.('data-retailer')
    if (attr) return attr
    const classList = (button as HTMLElement).className || ''
    if (classList.includes('penguin')) return 'Penguin Random House'
    if (classList.includes('amazon')) return 'Amazon'
    if (classList.includes('barnes')) return 'Barnes & Noble'
    if (classList.includes('walmart')) return 'Walmart'
    if (classList.includes('bookshop')) return 'Bookshop'
    if (classList.includes('target')) return 'Target'
    return 'Unknown Retailer'
  }

  private trackNewsletterSubscriptions(): void {
    const newsletterForm = document.querySelector<HTMLFormElement>('.newsletter-form')
    if (newsletterForm) {
      newsletterForm.addEventListener('submit', () => {
        this.trackEvent('newsletter_subscription', {
          page_section: 'newsletter_section',
          form_location: 'main_newsletter'
        })
      })
    }
  }

  private getNavigationType(link: Element): string {
    if (link.closest('nav')) return 'main_navigation'
    const footerGroup = (link as HTMLElement).closest('[data-footer-nav]') as HTMLElement | null
    if (footerGroup) {
      const side = footerGroup.getAttribute('data-footer-nav')
      return side === 'left' ? 'footer_left' : side === 'right' ? 'footer_right' : 'footer'
    }
    return 'other'
  }

  private trackSectionViews(): void {
    const sections = [
      { id: 'hero', name: 'Hero' },
      { id: 'order-header', name: 'Order / Get the Book' },
      { id: 'media', name: 'In the Media' },
      { id: 'subscribe', name: 'Subscribe / Free Guides' },
      { id: 'about-author', name: 'About the Author' },
      { id: 'instagram', name: 'Instagram' },
      { id: 'contact', name: 'Contact' },
      { id: 'about-book', name: 'About the Book' },
      { id: 'newsletter', name: 'Newsletter' },
    ]
    const seen = new Set<string>()
    let scrollBeyondHeroFired = false

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const id = (entry.target as HTMLElement).id
        if (!id || seen.has(id)) return

        seen.add(id)
        const section = sections.find((s) => s.id === id)
        const sectionName = section?.name ?? id

        this.trackEvent('view_section', {
          section_id: id,
          section_name: sectionName,
          section_title: document.title,
        })

        if (id !== 'hero' && !scrollBeyondHeroFired) {
          scrollBeyondHeroFired = true
          this.trackEvent('scroll_beyond_hero', {
            first_section_id: id,
            first_section_name: sectionName,
          })
        }
      })
    }, { threshold: 0.25 })

    const observeInterval = setInterval(() => {
      let observedCount = 0
      sections.forEach(({ id }) => {
        const el = document.getElementById(id)
        if (el) {
          observer.observe(el)
          observedCount++
        }
      })
      if (observedCount >= sections.length - 2) clearInterval(observeInterval)
    }, 500)

    // Safety clear
    setTimeout(() => clearInterval(observeInterval), 10000)
  }

  private getSocialPlatform(link: HTMLAnchorElement): string {
    const href = (link.href || '').toLowerCase()
    if (href.includes('instagram')) return 'Instagram'
    if (href.includes('twitter')) return 'Twitter'
    if (href.includes('linkedin')) return 'LinkedIn'
    if (href.includes('facebook')) return 'Facebook'
    return 'Unknown Platform'
  }

  private trackTimeOnPage(): void {
    const startTime = Date.now()
    const thresholds = [30, 60, 120, 300]
    const tracked = new Set<number>()

    setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000)
      thresholds.forEach((t) => {
        if (elapsed >= t && !tracked.has(t)) {
          tracked.add(t)
          this.trackEvent('engagement_time_milestone', {
            milestone_seconds: t,
            milestone_label: t >= 60 ? `${t / 60}m` : `${t}s`,
            page_title: document.title
          })
        }
      })
    }, 10000)
  }

  trackEvent(eventName: string, parameters: Record<string, any> = {}): void {
    const enriched = {
      ...this.getCampaignEventParameters(),
      ...parameters,
      timestamp: new Date().toISOString(),
      page_url: window.location.href
    }

    // 1. Dispatch internal events (for dev overlay)
    try {
      const detail = { event: eventName, parameters: enriched }
      window.dispatchEvent(new CustomEvent('analytics:event', { detail }))
      window.__analyticsEvents = window.__analyticsEvents || []
      window.__analyticsEvents.push({ ts: Date.now(), ...detail })
      if (window.__analyticsEvents.length > 200) window.__analyticsEvents.shift()
    } catch { }

    // 2. Log to console if in debug mode
    if (this.config.debug) {
      console.log(`📊 [GA4 DEBUG] ${eventName}:`, enriched)
      return // CRITICAL: Stop here in debug mode
    }

    // 3. Send to GA if enabled and initialized
    if (window.gtag) {
      window.gtag('event', eventName, enriched)
    }
  }

  trackFormSubmission(formType: string, success: boolean = true, errorMessage: string | null = null): void {
    this.trackEvent('form_submission', {
      form_type: formType,
      success,
      error_message: errorMessage,
      page_section: this.getCurrentPageSection()
    })
  }

  private getCurrentPageSection(): string {
    const hash = window.location.hash
    if (hash.includes('about-book')) return 'about_book'
    if (hash.includes('about-author')) return 'about_author'
    if (hash.includes('order')) return 'order_section'
    if (hash.includes('newsletter')) return 'newsletter'
    if (hash.includes('contact')) return 'contact'
    return 'hero'
  }

  trackOutboundLink(url: string, linkText: string): void {
    let link_domain = ''
    try {
      link_domain = new URL(url).hostname
    } catch { }

    this.trackEvent('outbound_link_click', {
      link_url: url,
      link_text: linkText,
      link_domain,
      page_section: this.getCurrentPageSection()
    })
  }
}

export default AnalyticsManager

